import React from 'react';
import { cn } from '@rocco/utils/cn';
import { PROPOSAL_TYPO_CLASS } from '~/modules/ecommerce/component/proposal/proposal-styles';

/** Placeholder image for team members without an avatar. Matches the email template. */
const TEAM_MEMBER_PLACEHOLDER_URL =
    '/_resources/themes/archipro2/images/professionals/TeamMember-Placeholder.jpg';

/** Data shape for a deal owner as stored in InvoiceColumnProps. */
export interface DealOwnerData {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    bookingUrl?: string;
    avatarUrl?: string;
}

/** Visual variant controlling avatar size, text size and spacing. */
type TeamMemberCardVariant = 'default' | 'proposal';

interface TeamMemberCardProps {
    /** Display name (bold heading). */
    name: string;
    /** Email address shown below the name. */
    email?: string;
    /** Phone number shown below the email. */
    phone?: string;
    /** URL for the circular avatar image. Falls back to a grey placeholder. */
    avatarUrl?: string;
    /** When true and bookingUrl is provided, renders an underlined "Book a Meeting" link. */
    showBookingLink?: boolean;
    /** Absolute or relative URL for the booking link. */
    bookingUrl?: string;
    /** When true, email and phone render as plain text (no links) and booking link is hidden. */
    nonInteractive?: boolean;
    /** Visual variant: 'default' (large avatar & text) or 'proposal' (smaller, denser layout). */
    variant?: TeamMemberCardVariant;
    /**
     * When true, vertically centres the name block with the avatar (useful for single-line
     * placeholders such as "No deal owner set").
     */
    middleAligned?: boolean;
    className?: string;
}

/** Maps a DealOwnerData object (from InvoiceColumnProps) to TeamMemberCardProps. */
export function mapDealOwnerToCardProps(
    dealOwner: DealOwnerData,
): TeamMemberCardProps {
    return {
        name: dealOwner.name,
        email: dealOwner.email,
        phone: dealOwner.phone,
        avatarUrl: dealOwner.avatarUrl,
        showBookingLink: !!dealOwner.bookingUrl,
        bookingUrl: dealOwner.bookingUrl,
    };
}

/**
 * Reusable card displaying a team member's avatar, name, contact info
 * and an optional "Book a Meeting" link. Designed for use on invoices
 * and anywhere a compact team-member summary is needed.
 */
const VARIANT_STYLES = {
    default: {
        root: 'gap-6',
        avatar: 'h-24 w-24',
        avatarIcon: 'h-9 w-9',
        name: 'font-display text-[1.25rem] font-semibold leading-[140%] text-[#161616]',
        detail: 'font-display text-3.5 leading-[140%] text-[#141414] no-underline hover:cursor-pointer hover:underline',
        booking:
            'mt-2 font-display text-3.5 font-medium leading-[140%] text-[#161616] underline underline-offset-2 hover:text-[#444]',
    },
    proposal: {
        root: 'gap-4',
        avatar: 'h-[4.5rem] w-[4.5rem]',
        name: '',
        detail: 'no-underline hover:underline hover:cursor-pointer leading-[1.15]',
        booking: '!underline underline-offset-2 mt-2 leading-[1.3]',
    },
} as const;

const TeamMemberCard = ({
    name,
    email,
    phone,
    avatarUrl,
    showBookingLink = false,
    bookingUrl,
    nonInteractive = false,
    variant = 'default',
    middleAligned = false,
    className,
}: TeamMemberCardProps) => {
    const styles = VARIANT_STYLES[variant];

    return (
        <div
            className={cn(
                'flex',
                middleAligned ? 'items-center' : 'items-start',
                styles.root,
                className,
            )}
        >
            {/* Avatar */}
            <div
                className={cn(
                    'shrink-0 overflow-hidden rounded-full bg-[#E5E5E5]',
                    styles.avatar,
                )}
            >
                <img
                    src={avatarUrl?.trim() || TEAM_MEMBER_PLACEHOLDER_URL}
                    alt={name}
                    className="h-full w-full object-cover"
                />
            </div>

            {/* Info */}
            <div
                className={cn(
                    'flex h-full flex-col justify-center gap-0.5 md:pl-[0.33rem]',
                    middleAligned ? 'py-0' : 'md:py-[0.56rem]',
                )}
            >
                <span
                    className={cn(
                        PROPOSAL_TYPO_CLASS.CLIENT_DIRECTOR_NAME,
                        styles.name,
                    )}
                >
                    {name}
                </span>
                {email &&
                    (nonInteractive ? (
                        <span
                            className={cn(
                                PROPOSAL_TYPO_CLASS.CLIENT_DIRECTOR_INFO,
                                styles.detail,
                                'cursor-default hover:no-underline',
                            )}
                        >
                            {email}
                        </span>
                    ) : (
                        <a
                            href={`mailto:${email}`}
                            className={cn(
                                PROPOSAL_TYPO_CLASS.CLIENT_DIRECTOR_INFO,
                                styles.detail,
                            )}
                        >
                            {email}
                        </a>
                    ))}
                {phone &&
                    (nonInteractive ? (
                        <span
                            className={cn(
                                PROPOSAL_TYPO_CLASS.CLIENT_DIRECTOR_INFO,
                                styles.detail,
                                'cursor-default hover:no-underline',
                            )}
                        >
                            {phone}
                        </span>
                    ) : (
                        <a
                            href={`tel:${phone}`}
                            className={cn(
                                PROPOSAL_TYPO_CLASS.CLIENT_DIRECTOR_INFO,
                                styles.detail,
                            )}
                        >
                            {phone}
                        </a>
                    ))}
                {!nonInteractive && showBookingLink && bookingUrl && (
                    <a
                        href={bookingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                            PROPOSAL_TYPO_CLASS.CLIENT_DIRECTOR_BOOK_A_MEETING,
                            styles.booking,
                        )}
                    >
                        Book a Meeting
                    </a>
                )}
            </div>
        </div>
    );
};

export default TeamMemberCard;
